import { useAuth } from 'auth';
import CenterMapButton from 'components/CenterMap/CenterMapButton';
import MapViewTypeToggle from 'components/MapViewTypeToggle';
import ZoomInOut from 'components/ZoomInOut/ZoomInOut';
import { MapboxStyles } from 'constants/map';
import ECSMapContext, { ECSMapContextContent } from 'contexts/ECSMapContext';
import ECSWorldContext from 'contexts/ECSWorldContext';
import MarkupKitContext from 'contexts/MarkupKitContext';
import Canvas from 'editor/components/Canvas';
import MapboxCamera from 'editor/components/map/MapboxCamera';
import MapboxMap from 'editor/components/map/MapboxMap';
import MapboxOverlay from 'editor/components/map/MapboxOverlay';
import MapboxTool, { MapboxTools } from 'editor/components/map/MapboxTool';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { OrderedMap } from 'immutable';
import mapboxgl from 'mapbox-gl';
import { useMapLayersManager } from 'providers/MapLayersManagerProvider';
import React, {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SheetEntry } from 'records/AppState';
import { Role } from 'records/Role';
import { getProjectBounds, getProjectViewport } from 'utils/map';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import addMapSystems from './addMapSystems';

const HEADER_HEIGHT = 96;
const mapContainerId = 'view-map-layers-overlay';

interface Props extends WithAppStoreProps {
  mapHeight: string | number;
  buttonLeftShift: number;
}

const ECSMap: FC<PropsWithChildren<Props>> = (
  props: PropsWithChildren<Props>
) => {
  const { userId } = useAuth();
  const { appState, children, mapHeight, buttonLeftShift } = props;
  const [mapComps, setMapComps] = useState<ECSMapContextContent>();
  const [mapStyle, setMapStyle] = useState<MapboxStyles>(MapboxStyles.street);
  const [showMapViewToggle, setShowMapViewToggle] = useState<boolean>(false);
  const markupsOverlayCanvasEl = useRef<HTMLCanvasElement>(null);
  const world = useContext(ECSWorldContext);
  const markupKit = useContext(MarkupKitContext);

  const mapLayersManager = useMapLayersManager();
  const currentMappedSheets = mapLayersManager.getCurrentMappedSheetEntries();
  const visibleMappedSheets = appState.getVisibleSheetEntries();
  const { geoLocation } = appState;
  const {
    [FeatureFlag.FitProjectToScreenButton]: isFitProjectToScreenButtonEnabled,
  } = useFeatureFlags();

  const updateBoundingBox = (mappedSheets: OrderedMap<string, SheetEntry>) => {
    const mapboxCameraComp = world.grab(MapboxCamera)?.component;
    const map = world.grab(MapboxMap)?.component.map;
    if (map && mapboxCameraComp) {
      const projectViewport = getProjectViewport(
        geoLocation,
        mappedSheets,
        HEADER_HEIGHT,
        mapContainerId
      );

      const { latitude, longitude, bearing, pitch, zoom } = projectViewport;

      mapboxCameraComp.tracked.viewport = {
        latitude,
        longitude,
        bearing,
        pitch,
        zoom,
      };
    }

    world.systems.run();
  };

  const flyToBoundingBox = (mappedSheets: OrderedMap<string, SheetEntry>) => {
    const mapboxCameraComp = world.grab(MapboxCamera)?.component;
    const map = world.grab(MapboxMap)?.component.map;

    if (map && mapboxCameraComp) {
      const projectBounds = getProjectBounds(geoLocation, mappedSheets);
      const mapLayersOverlay = document.querySelector(
        '#view-map-layers-overlay'
      );
      const mapLayersOverlayWidth = mapLayersOverlay?.clientWidth ?? 0;
      const rfisOverlayWidth = document.querySelector(
        '#view-rfi-list-overlay'
      )?.clientWidth;
      const punchesOverlayWidth = document.querySelector(
        '#view-punch-list-overlay'
      )?.clientWidth;
      const rightOffset = (rfisOverlayWidth || punchesOverlayWidth) ?? 0;
      if (projectBounds) {
        mapboxCameraComp.tracked.fitToBounds = {
          bounds: projectBounds,
          padding: {
            top: 0,
            bottom: 0,
            right: rightOffset,
            left: mapLayersOverlayWidth,
          },
        };
      }
    }

    world.systems.run();
  };

  useEffect(() => {
    async function createMap() {
      const map = new MapboxMap();
      const tool = new MapboxTool(MapboxTools.Pointer);

      addMapSystems(world);

      if (process.env.NODE_ENV === 'development') {
        // @ts-ignore
        window.mapWorld = world;

        // @ts-ignore
        window.mapComp = map;
      }

      world.createEntity().add(markupKit).addTag('markupKit');

      const projectViewport = getProjectViewport(
        geoLocation,
        currentMappedSheets,
        HEADER_HEIGHT,
        mapContainerId
      );

      world
        .createEntity()
        .add(map)
        .add(new MapboxCamera(projectViewport))
        .add(tool);

      if (!markupsOverlayCanvasEl.current) return;

      // Create markups canvas layer. Might be moved in the future.
      const markupsOverlayCanvas = new Canvas();

      markupsOverlayCanvas.canvas = markupsOverlayCanvasEl.current;
      markupsOverlayCanvas.clear = true;
      markupsOverlayCanvas.monitorResize = true;

      // Markups overlay
      const markupsOverlay = new MapboxOverlay();

      world.createEntity().add(markupsOverlayCanvas);

      // Saving the user's role on an entity so that it may be accessed within
      // the ECS domain.
      const userRole = appState.roles.get(userId, new Role());

      world.createEntity().add(userRole);

      setMapComps({
        map,
        markupsOverlay,
        markupsOverlayCanvas,
      });

      world.systems.run(); // prime the pump.

      if (currentMappedSheets.size > 0) {
        updateBoundingBox(currentMappedSheets);
      }
    }

    createMap();
  }, [geoLocation]);

  useEffect(() => {
    return () => {
      // destroy MapboxCamera entity on geoLocation change issue #BBCLD-3381
      // fixes problem with zoom buttons and center button
      world.locateAll(MapboxCamera)?.forEach((e) => e.destroy());
    };
  }, [world, geoLocation]);

  useEffect(() => {
    return () => {
      if (mapComps?.map.map) {
        mapComps?.map.map?.remove();
        mapComps.map.map = null;
      }
      // TEMP: Making sure to cleanup markups overlay canvas entity.
      world.locateAll(Canvas).forEach((e) => e.destroy());

      // TODO: Add world.destroy() to Ecstatic
    };
  }, [world, mapComps]);

  useEffect(() => {
    if (mapComps?.map) {
      mapComps.map.style = mapStyle;

      world.systems.run();
    }
  }, [world, mapStyle, mapComps]);

  const getMapViewport = (map: mapboxgl.Map): Viewport => {
    const zoom = map.getZoom();
    const center = map.getCenter();
    const bearing = map.getBearing();
    const pitch = map.getPitch();

    return {
      zoom,
      latitude: center.lat,
      longitude: center.lng,
      bearing,
      pitch,
    };
  };

  const zoomMap = (zoomIn: boolean) => {
    const mapboxCameraComp = world.grab(MapboxCamera)?.component;
    const map = world.grab(MapboxMap)?.component.map;

    if (map && mapboxCameraComp) {
      const viewPort = getMapViewport(map);

      const { latitude, longitude, bearing, pitch, zoom } = viewPort;

      const zoomIncrement = zoomIn ? 1 : -1;
      mapboxCameraComp.tracked.viewport = {
        latitude,
        longitude,
        bearing,
        pitch,
        zoom: zoom + zoomIncrement,
      };
    }

    world.systems.run();
  };

  const centerMap = () => {
    if (visibleMappedSheets.size > 0) {
      flyToBoundingBox(visibleMappedSheets);
    } else {
      flyToBoundingBox(currentMappedSheets);
    }
  };

  return (
    <div className="pos-r">
      <style>
        {`
          .mapboxgl-canvas, #map-container, #map-markups-overlay {
            outline: none;
          }
        `}
      </style>
      <div
        id="map-container"
        style={{ height: mapHeight }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {mapComps ? (
          <ECSMapContext.Provider value={mapComps}>
            {children}
          </ECSMapContext.Provider>
        ) : null}
      </div>
      <canvas
        ref={markupsOverlayCanvasEl}
        className="pos-a"
        id="map-markups-overlay"
        style={{
          width: '100vw',
          height: mapHeight,
          top: 0,
          left: 0,
          pointerEvents: 'none',
        }}
      />
      {isFitProjectToScreenButtonEnabled && (
        <CenterMapButton
          id="map"
          disable={appState.getCurrentMappedSheetEntries().size === 0}
          handleCenterMap={centerMap}
          style={{
            left: buttonLeftShift + 17,
          }}
        />
      )}
      <ZoomInOut
        id="map"
        handleZoomIn={() => zoomMap(true)}
        handleZoomOut={() => zoomMap(false)}
        style={{ left: buttonLeftShift + 17 }}
      />
      <MapViewTypeToggle
        id="map-view"
        includeNoMap
        mapStyle={mapStyle}
        onStyleChange={setMapStyle}
        mapPosition={{ left: buttonLeftShift + 78 }}
        showMenu={showMapViewToggle}
        toggleMenu={() => setShowMapViewToggle(!showMapViewToggle)}
      />
    </div>
  );
};

export default withAppStore<PropsWithChildren<Props>>(ECSMap);
